import axios from 'axios';
import { useAuthStore } from '../stores/auth';
import { tenantId } from '../tenant/tenantId';
import defaultStrings from '../tenant/defaultStrings';

const BASE_URL = process.env.REACT_APP_PUBLIC_API_URL || 'http://localhost:5002';

const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

apiClient.interceptors.request.use((config) => {
  if (tenantId) {
    config.headers['X-Tenant-Host'] = tenantId;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      const { isAuthenticated, clearAuth } = useAuthStore.getState();
      if (isAuthenticated) {
        clearAuth();
        window.dispatchEvent(new CustomEvent('session-expired'));
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Generic API request wrapper that standardizes the response format.
 * 
 * @param {string} endpoint - The API endpoint (e.g., '/auth/login')
 * @param {Object} options - Fetch-style options (method, body, headers)
 * @param {Function|null} decoder - Custom decoder function for type validation
 * @returns {Promise<{success: boolean, data?: any, error?: string, status?: number}>}
 */
export async function apiRequest(endpoint, options = {}, decoder = null) {
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  const axiosConfig = {
    method: options.method || 'GET',
    url: path,
    headers: options.headers || {},
  };

  if (options.body) {
    axiosConfig.data = options.body;
  }
  if (options.timeout !== undefined) {
    axiosConfig.timeout = options.timeout;
  }
  if (options.onUploadProgress) {
    axiosConfig.onUploadProgress = options.onUploadProgress;
  }

  try {
    const response = await apiClient(axiosConfig);

    if (response.status === 204) {
      return { success: true, data: {}, status: response.status };
    }

    if (decoder) {
      const decoded = decoder(response.data);
      if (decoded === null) {
        return {
          success: false,
          error: defaultStrings.common.genericError,
          status: response.status,
        };
      }
      return {
        success: true,
        data: decoded,
        status: response.status,
      };
    }

    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    const status = error.response?.status;
    const serverMessage = error.response?.data?.message;
    let errorMessage = defaultStrings.common.genericError;
    if (!error.response) {
      errorMessage = defaultStrings.common.networkError;
    } else if (typeof serverMessage === 'string' && serverMessage.trim() && status < 500) {
      errorMessage = serverMessage;
    }

    const details = error.response?.data?.details;
    return {
      success: false,
      error: errorMessage,
      code: typeof error.response?.data?.code === 'string' ? error.response.data.code : undefined,
      details: details && typeof details === 'object' ? details : undefined,
      retryAfterMs:
        typeof error.response?.data?.retryAfterMs === 'number' ? error.response.data.retryAfterMs : undefined,
      status: status,
    };
  }
}
